import { IComboBox, IComboBoxOption, Icon, IDropdownOption, Stack, Text } from '@fluentui/react';
import { Button, buttonStylesGhost, buttonStylesPrimary, ComboBox, Dropdown, useTheme } from '@h2oai/ui-kit';

import { Grouping as H2OGPTeGrouping } from '../../../telemetry/gen/ai/h2o/cloud/telemetry_read_api/h2ogpte/v1/h2ogpte_pb';
import { Granularity, Grouping } from '../../../telemetry/gen/ai/h2o/cloud/telemetry_read_api/v1/aiunits_pb';
import { granularityOptions } from '../constants/granularity';
import { groupByOptions } from '../constants/grouping';
import { h2ogpteGroupByOptions } from '../constants/h2ogpteGrouping';
import { resourceOptions, ResourceType } from '../constants/resource';
import { TokenUsageType, tokenUsageTypeOptions } from '../constants/tokenUsageType';
import {
  DateRangeType,
  ModelFilterOption,
  ServiceFilterOption,
  TagFilterOption,
  UserFilterOption,
  WorkspaceFilterOption,
} from '../types';
import { formatLocalDate } from '../utils';

// Date range type labels mapping
const dateRangeTypeLabels: Record<DateRangeType, string> = {
  '1day': '1 Day',
  '7days': '7 Days',
  mtd: 'MTD',
  '1month': '1 Month',
  '3months': '3 Months',
  '6months': '6 Months',
  '1year': '1 Year',
  '3years': '3 Years',
  ytd: 'YTD',
  custom: '',
};

function getDateRangeSummary(dateRangeType: DateRangeType, granularity: Granularity): React.ReactNode {
  const granularityLabel =
    granularityOptions.find((option) => option.key === granularity)?.label.toLowerCase() || 'daily';

  // For custom range, only show granularity
  if (dateRangeType === 'custom') {
    return (
      <>
        Showing <em>{granularityLabel}</em> usage
      </>
    );
  }

  const rangeLabel = dateRangeTypeLabels[dateRangeType];
  return (
    <>
      Showing <em>{rangeLabel}</em> with <em>{granularityLabel}</em> granularity
    </>
  );
}

interface FilterPanelProps {
  // Resource type props
  resourceType: ResourceType;
  onChangeResource: (event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption) => void;
  isResourceDisabled: boolean;

  // Date range props
  startTime: Date;
  endTime: Date;
  granularity: Granularity;
  dateRangeType: DateRangeType;
  onToggleDateRangeCallout: (event: React.MouseEvent<HTMLElement>) => void;

  // Group by props (AI Units)
  groupBy: Grouping;
  onChangeGroupBy: (event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption) => void;

  // H2OGPTe-specific props
  h2ogpteGroupBy?: H2OGPTeGrouping;
  onChangeH2OGPTeGroupBy?: (event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption) => void;
  tokenUsageType?: TokenUsageType;
  onChangeTokenUsageType?: (event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption) => void;

  // Workspace filter props
  workspaceOptions: WorkspaceFilterOption[];
  selectedWorkspaces: string[];
  onChangeWorkspaceFilter: (event: React.FormEvent<IComboBox>, option?: IComboBoxOption) => void;

  // User filter props
  userOptions: UserFilterOption[];
  selectedUsers: string[];
  onChangeUserFilter: (event: React.FormEvent<IComboBox>, option?: IComboBoxOption) => void;

  // Service filter props (AI Units only)
  serviceOptions: ServiceFilterOption[];
  selectedServices: string[];
  onChangeServiceFilter: (event: React.FormEvent<IComboBox>, option?: IComboBoxOption) => void;

  // Tag filter props (AI Units only)
  tagOptions: TagFilterOption[];
  selectedTags: string[];
  onChangeTagFilter: (event: React.FormEvent<IComboBox>, option?: IComboBoxOption) => void;

  // Model filter props (H2OGPTe only)
  modelOptions?: ModelFilterOption[];
  selectedModels?: string[];
  onChangeModelFilter?: (event: React.FormEvent<IComboBox>, option?: IComboBoxOption) => void;

  // Shared filter actions
  onApplyFilters: () => void;
  onResetFilters: () => void;
  hasFilterChanges: boolean;
  hasFiltersToReset: boolean;
}

/**
 * FilterPanel component - Right sidebar with controls for filtering and grouping data
 *
 * Contains:
 * - Date range selector button
 * - Group by dropdown
 * - Workspace, User, and Service filters with multi-select and Apply/Reset buttons
 */
export function FilterPanel({
  resourceType,
  onChangeResource,
  isResourceDisabled,
  startTime,
  endTime,
  granularity,
  dateRangeType,
  onToggleDateRangeCallout,
  groupBy,
  onChangeGroupBy,
  h2ogpteGroupBy,
  onChangeH2OGPTeGroupBy,
  tokenUsageType,
  onChangeTokenUsageType,
  workspaceOptions,
  selectedWorkspaces,
  onChangeWorkspaceFilter,
  userOptions,
  selectedUsers,
  onChangeUserFilter,
  serviceOptions,
  selectedServices,
  onChangeServiceFilter,
  tagOptions,
  selectedTags,
  onChangeTagFilter,
  modelOptions,
  selectedModels,
  onChangeModelFilter,
  onApplyFilters,
  onResetFilters,
  hasFilterChanges,
  hasFiltersToReset,
}: FilterPanelProps) {
  const isH2OGPTe = resourceType === ResourceType.H2OGPTE_TOKENS;

  const theme = useTheme();

  return (
    <Stack.Item disableShrink styles={{ root: { width: 280 } }}>
      <Stack tokens={{ childrenGap: 16 }}>
        {/* Chart Controls */}
        <div
          style={{
            background: theme.palette?.white,
            padding: 16,
            borderRadius: 4,
          }}
        >
          <Stack tokens={{ childrenGap: 16 }}>
            {/* Resource Type Dropdown */}
            <div>
              <label
                htmlFor="resource-dropdown"
                style={{
                  fontSize: '14px',
                  fontWeight: 400,
                  color: theme.palette?.black,
                  padding: '5px 0px',
                  display: 'block',
                }}
              >
                Resource
              </label>
              <Dropdown
                id="resource-dropdown"
                ariaLabel="Change resource type"
                styles={{ root: { width: '100%' } }}
                options={resourceOptions}
                selectedKey={resourceType}
                onChange={onChangeResource}
              />
            </div>

            {/* Date Range Button */}
            <div>
              <label
                htmlFor="date-range-button"
                style={{
                  fontSize: '14px',
                  fontWeight: 400,
                  color: theme.palette?.black,
                  padding: '5px 0px',
                  display: 'block',
                }}
              >
                Date Range
              </label>
              <button
                id="date-range-button"
                type="button"
                disabled={isResourceDisabled}
                onClick={isResourceDisabled ? undefined : onToggleDateRangeCallout}
                style={{
                  width: '100%',
                  height: '32px',
                  backgroundColor: isResourceDisabled ? theme.palette?.gray200 : theme.palette?.white,
                  border: `1px solid ${theme.palette?.gray300}`,
                  borderRadius: '2px',
                  cursor: isResourceDisabled ? 'not-allowed' : 'pointer',
                  padding: '0 28px 0 8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  fontSize: '14px',
                  color: isResourceDisabled ? theme.palette?.gray600 : theme.palette?.black,
                  textAlign: 'left',
                  position: 'relative',
                  opacity: isResourceDisabled ? 0.6 : 1,
                }}
                onMouseEnter={(e) => {
                  if (!isResourceDisabled) {
                    e.currentTarget.style.borderColor = theme.palette?.gray900 || 'black';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = theme.palette?.gray300 || 'black';
                }}
              >
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {formatLocalDate(startTime)} - {formatLocalDate(endTime)}
                </span>
                <Icon
                  iconName="Calendar"
                  styles={{
                    root: {
                      fontSize: '12px',
                      color: theme.palette?.black,
                      position: 'absolute',
                      right: '8px',
                    },
                  }}
                />
              </button>
              {/* Date Range Summary */}
              <div style={{ height: 8 }}></div>
              <Text
                styles={{
                  root: {
                    color: theme.palette?.black,
                    fontSize: 13,
                    marginTop: 16,
                  },
                }}
              >
                {getDateRangeSummary(dateRangeType, granularity)}
              </Text>
            </div>

            {/* Group By dropdown - different options for AI Units vs H2OGPTe */}
            {isH2OGPTe ? (
              <Dropdown
                ariaLabel="Change group by"
                styles={{ root: { width: '100%' } }}
                label="Group By"
                options={h2ogpteGroupByOptions}
                selectedKey={h2ogpteGroupBy}
                onChange={onChangeH2OGPTeGroupBy}
                disabled={isResourceDisabled}
              />
            ) : (
              <Dropdown
                ariaLabel="Change group by"
                styles={{ root: { width: '100%' } }}
                label="Group By"
                options={groupByOptions}
                selectedKey={groupBy}
                onChange={onChangeGroupBy}
                disabled={isResourceDisabled}
              />
            )}

            {/* Token Usage Type dropdown - only for H2OGPTe */}
            {isH2OGPTe && (
              <Dropdown
                ariaLabel="Change token usage type"
                styles={{ root: { width: '100%' } }}
                label="Token Usage"
                options={tokenUsageTypeOptions}
                selectedKey={tokenUsageType}
                onChange={onChangeTokenUsageType}
                disabled={isResourceDisabled}
              />
            )}
          </Stack>
        </div>

        {/* Filters Section */}
        <div
          style={{
            background: 'white',
            padding: 16,
            borderRadius: 4,
          }}
        >
          <Stack tokens={{ childrenGap: 12 }}>
            <Text variant="medium" styles={{ root: { fontWeight: 600 } }}>
              Filters
            </Text>
            <ComboBox
              multiSelect
              ariaLabel="Workspace filter"
              label="Workspace"
              placeholder="Select workspaces"
              options={workspaceOptions}
              selectedKey={selectedWorkspaces}
              onChange={onChangeWorkspaceFilter}
              disabled={isResourceDisabled}
              styles={{ root: { width: '100%' } }}
            />
            <ComboBox
              multiSelect
              ariaLabel="User filter"
              label="User"
              placeholder="Select users"
              options={userOptions}
              selectedKey={selectedUsers}
              onChange={onChangeUserFilter}
              disabled={isResourceDisabled}
              styles={{ root: { width: '100%' } }}
            />
            {/* Conditional filters based on resource type */}
            {isH2OGPTe ? (
              /* H2OGPTe: Model filter */
              <ComboBox
                multiSelect
                ariaLabel="Model filter"
                label="Model"
                placeholder="Select models"
                options={modelOptions || []}
                selectedKey={selectedModels || []}
                onChange={onChangeModelFilter}
                disabled={isResourceDisabled}
                styles={{ root: { width: '100%' } }}
              />
            ) : (
              /* AI Units: Service and Tag filters */
              <>
                <ComboBox
                  multiSelect
                  ariaLabel="Service filter"
                  label="Service"
                  placeholder="Select services"
                  options={serviceOptions}
                  selectedKey={selectedServices}
                  onChange={onChangeServiceFilter}
                  disabled={isResourceDisabled}
                  styles={{ root: { width: '100%' } }}
                />
                <ComboBox
                  multiSelect
                  ariaLabel="Tag filter"
                  label="Tag"
                  placeholder="Select tags"
                  options={tagOptions}
                  selectedKey={selectedTags}
                  onChange={onChangeTagFilter}
                  disabled={isResourceDisabled}
                  styles={{ root: { width: '100%' } }}
                />
              </>
            )}

            <div style={{ height: 24 }}></div>

            {/* Apply and Reset buttons */}
            <Stack horizontal horizontalAlign="end" tokens={{ childrenGap: 8 }}>
              <Button
                styles={buttonStylesGhost}
                text="Reset"
                onClick={onResetFilters}
                disabled={!hasFiltersToReset || isResourceDisabled}
              />
              <Button
                styles={buttonStylesPrimary}
                text="Apply"
                onClick={onApplyFilters}
                disabled={!hasFilterChanges || isResourceDisabled}
              />
            </Stack>
          </Stack>
        </div>
      </Stack>
    </Stack.Item>
  );
}
